import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Email as EmailIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  verifyEmailAsync,
  resendVerificationAsync,
  selectSignup,
  clearError,
} from '../../store/signupSlice';

const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { email, emailVerified, loading, error } = useAppSelector(selectSignup);

  const [resendSuccess, setResendSuccess] = useState(false);
  const [verifyingToken, setVerifyingToken] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyToken(token);
    }
  }, [token]);

  useEffect(() => {
    if (emailVerified) {
      // Redirect to onboarding after a brief delay
      const timer = setTimeout(() => {
        navigate('/onboarding');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [emailVerified, navigate]);

  const verifyToken = async (token: string) => {
    setVerifyingToken(true);
    try {
      await dispatch(verifyEmailAsync(token)).unwrap();
    } catch (err) {
      // Error handled by Redux
    } finally {
      setVerifyingToken(false);
    }
  };

  const handleResend = async () => {
    if (!email) return;

    dispatch(clearError());
    setResendSuccess(false);

    try {
      await dispatch(resendVerificationAsync(email)).unwrap();
      setResendSuccess(true);
    } catch (err) {
      // Error handled by Redux
    }
  };

  if (emailVerified) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckIcon sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            <Typography variant="h4" gutterBottom fontWeight={600}>
              Email Verified!
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Your email has been verified successfully.
              Redirecting you to the onboarding wizard...
            </Typography>
            <CircularProgress size={24} />
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (verifyingToken) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 3 }} />
            <Typography variant="h5" gutterBottom>
              Verifying your email...
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <EmailIcon sx={{ fontSize: 48, color: 'white' }} />
          </Box>

          <Typography variant="h4" gutterBottom fontWeight={600}>
            Check Your Email
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 2 }}>
            We've sent a verification link to:
          </Typography>

          <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
            {email || 'your email address'}
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 4 }}>
            Click the link in the email to verify your account and continue
            with the setup process.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {resendSuccess && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Verification email sent! Please check your inbox.
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleResend}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Resend Verification Email'}
            </Button>

            <Typography variant="body2" color="text.secondary">
              Didn't receive the email? Check your spam folder or try resending.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default VerifyEmailPage;
